import { ApolloQueueContext } from './ApolloQueueContext'
import React, { useMemo, useState, useEffect } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import type { ApolloClient } from '@wora/apollo-offline'
import { useConnection } from 'react-detect-offline'
import { removeExpiredStoredMutations } from '../util/removeExpiredStoredMutations'
import { useInterval } from '@paintscout/ui'

/**
 * Sends the offline request queue when user goes back online
 */
export default function ApolloQueue({ children }: { children?: React.ReactNode }) {
  const apolloClient = useApolloClient() as ApolloClient
  const { online } = useConnection()

  const [processing, setProcessing] = useState(false)
  const [lastProcess, setLastProcess] = useState(0)

  async function sendQueue() {
    const hasMutations = apolloClient.getStoreOffline().getListMutation().length > 0

    if (hasMutations) {
      setProcessing(true)

      removeExpiredStoredMutations(apolloClient)

      await apolloClient.getStoreOffline().process()
      setProcessing(false)
      setLastProcess(Date.now())
    }
  }

  useEffect(() => {
    if (online && !processing) {
      sendQueue()
    }
  }, [online])

  useInterval(() => {
    if (online && !processing) {
      sendQueue()
    }
  }, 10000)

  const value = useMemo(() => ({ processing, lastProcess }), [processing, lastProcess])

  return <ApolloQueueContext.Provider value={value}>{children}</ApolloQueueContext.Provider>
}
